import { GlIcon, GlAnimatedLoaderIcon, GlButton, GlSafeHtmlDirective, GlTooltipDirective } from '@gitlab/ui';
import { translate, translatePlural, sprintf } from '@gitlab/ui/dist/utils/i18n';
import throttle from 'lodash/throttle';
import DuoChatContextItemSelections from '../duo_chat_context/duo_chat_context_item_selections/duo_chat_context_item_selections';
import { SELECTED_CONTEXT_ITEMS_DEFAULT_COLLAPSED, MESSAGE_MODEL_ROLES } from '../../constants';
import DocumentationSources from '../duo_chat_message_sources/duo_chat_message_sources';
import { renderDuoChatMarkdownPreview } from '../../markdown_renderer';
import MessageFeedback from './message_feedback';
import { CopyCodeElement } from './copy_code_element';
import { InsertCodeSnippetElement } from './insert_code_snippet_element';
import { concatUntilEmpty, checkClipboardPermissions } from './utils';
import { DUO_CODE_SCRIM_BOTTOM_CLASS, DUO_CODE_SCRIM_OFFSET, DUO_CODE_SCRIM_TOP_CLASS } from './constants';
import MessageMap from './message_types/message_map';
import __vue_normalize__ from 'vue-runtime-helpers/dist/normalize-component.js';

const i18n = {
  CHAT_MESSAGE_COPIED: translate('DuoChatMessage.chatMessageCopied', 'Copied'),
  CHAT_MESSAGE_COPY: translate('DuoChatMessage.chatMessageCopyToClipboard', 'Copy to clipboard'),
  FROM_ME: translate('DuoChatMessage.fromMe', 'Me:'),
  FROM_DUO: translate('DuoChatMessage.fromDuo', 'Duo:')
};
var script = {
  name: 'DuoChatMessage',
  i18n,
  safeHtmlConfigExtension: {
    ADD_TAGS: ['copy-code', 'insert-code-snippet']
  },
  components: {
    DocumentationSources,
    DuoChatContextItemSelections,
    MessageFeedback,
    MessageMap,
    GlIcon,
    GlAnimatedLoaderIcon,
    GlButton
  },
  directives: {
    SafeHtml: GlSafeHtmlDirective,
    GlTooltip: GlTooltipDirective
  },
  inject: {
    // Note, we likely might move away from Provide/Inject for this
    // and only ship the versions that are currently in the default
    // See https://gitlab.com/gitlab-org/gitlab-ui/-/merge_requests/3953#note_1762834219
    // for more context.
    renderGFM: {
      from: 'renderGFM',
      default: () => element => {
        element.classList.add('duo-chat-markdown', 'duo-chat-compact-markdown');
      }
    },
    renderMarkdown: {
      from: 'renderMarkdown',
      default: () => renderDuoChatMarkdownPreview
    }
  },
  props: {
    /**
     * A message object
     */
    message: {
      type: Object,
      required: true
    },
    isCancelled: {
      type: Boolean,
      required: true
    },
    /**
     * Base URL of the GitLab instance.
     */
    trustedUrls: {
      type: Array,
      required: false,
      default: () => []
    },
    /**
     * Whether the chat should show the feedback link on the assistant messages.
     */
    withFeedback: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data() {
    return {
      messageWatcher: null,
      // imperatively set up watcher on message
      messageChunks: [],
      selectedContextItemsDefaultCollapsed: SELECTED_CONTEXT_ITEMS_DEFAULT_COLLAPSED,
      hovered: false,
      copied: false
    };
  },
  computed: {
    isChunk() {
      return typeof this.message.chunkId === 'number';
    },
    isNotChunkOrCancelled() {
      return !this.isChunk || this.isCancelled;
    },
    isChunkAndNotCancelled() {
      return this.isChunk && !this.isCancelled;
    },
    isAssistantMessage() {
      return this.isMessageOfType(MESSAGE_MODEL_ROLES.assistant);
    },
    isUserMessage() {
      return this.isMessageOfType(MESSAGE_MODEL_ROLES.user);
    },
    sources() {
      var _this$message$extras;
      return (_this$message$extras = this.message.extras) === null || _this$message$extras === void 0 ? void 0 : _this$message$extras.sources;
    },
    hasFeedback() {
      var _this$message$extras2;
      return Boolean((_this$message$extras2 = this.message.extras) === null || _this$message$extras2 === void 0 ? void 0 : _this$message$extras2.hasFeedback);
    },
    defaultContent() {
      if (this.message.contentHtml) {
        return this.message.contentHtml;
      }
      return this.renderMarkdown(this.message.content, this.trustedUrls);
    },
    messageContent() {
      if (this.isAssistantMessage && this.isChunk) {
        return this.renderMarkdown(concatUntilEmpty(this.messageChunks), this.trustedUrls);
      }
      return this.renderMarkdown(this.defaultContent, this.trustedUrls) || this.renderMarkdown(concatUntilEmpty(this.message.chunks), this.trustedUrls);
    },
    renderedError() {
      var _this$message$errors;
      return this.renderMarkdown(((_this$message$errors = this.message.errors) === null || _this$message$errors === void 0 ? void 0 : _this$message$errors.join('; ')) || '', this.trustedUrls);
    },
    error() {
      var _this$message, _this$message$errors2;
      return Boolean((_this$message = this.message) === null || _this$message === void 0 ? void 0 : (_this$message$errors2 = _this$message.errors) === null || _this$message$errors2 === void 0 ? void 0 : _this$message$errors2.length) && this.message.errors.join('; ');
    },
    selectedContextItems() {
      var _this$message$extras3;
      return ((_this$message$extras3 = this.message.extras) === null || _this$message$extras3 === void 0 ? void 0 : _this$message$extras3.contextItems) || [];
    },
    displaySelectedContextItems() {
      return Boolean(this.selectedContextItems.length);
    },
    selectedContextItemsTitle() {
      if (!this.displaySelectedContextItems) return '';
      const count = this.selectedContextItems.length;
      if (this.isUserMessage) {
        return translatePlural('DuoChatMessage.SelectedContextItemsTitleUserMessage', 'Included reference', 'Included references')(count);
      }
      return sprintf(translatePlural('DuoChatMessage.SelectedContextItemsTitleAssistantMessage', 'Used %{count} included reference', 'Used %{count} included references')(count), {
        count
      });
    },
    shouldShowCopyAction() {
      return Boolean(navigator.clipboard && !this.isChunk && this.isAssistantMessage && this.hovered);
    },
    showA11yFromText() {
      return this.isAssistantMessage && !this.error || this.isUserMessage;
    },
    shouldShowFeedbackLink() {
      return this.withFeedback && this.isNotChunkOrCancelled && this.isAssistantMessage;
    }
  },
  beforeCreate() {
    if (!customElements.get('copy-code')) {
      customElements.define('copy-code', CopyCodeElement);
    }
    if (!customElements.get('insert-code-snippet')) {
      customElements.define('insert-code-snippet', InsertCodeSnippetElement);
    }
  },
  mounted() {
    if (this.isAssistantMessage) {
      // The watcher has to be created imperatively here
      // to give an opportunity to remove it after
      // the complete message has arrived
      this.messageWatcher = this.$watch('message', this.manageMessageUpdate);
    }
    this.setChunks();
    this.hydrateContentWithGFM();
  },
  updated() {
    this.hydrateContentWithGFM();
  },
  methods: {
    isMessageOfType(type) {
      var _this$message2, _this$message2$role, _this$message3, _this$message3$messag;
      return ((_this$message2 = this.message) === null || _this$message2 === void 0 ? void 0 : (_this$message2$role = _this$message2.role) === null || _this$message2$role === void 0 ? void 0 : _this$message2$role.toLowerCase()) === type || ((_this$message3 = this.message) === null || _this$message3 === void 0 ? void 0 : (_this$message3$messag = _this$message3.message_type) === null || _this$message3$messag === void 0 ? void 0 : _this$message3$messag.toLowerCase()) === type;
    },
    setChunks() {
      if (this.isChunk) {
        const {
          chunkId,
          content
        } = this.message;
        this.$set(this.messageChunks, chunkId - 1, content);
      } else {
        this.messageChunks = [];
      }
    },
    stopWatchingMessage() {
      if (this.messageWatcher) {
        this.messageWatcher(); // Stop watching the message prop
        this.messageWatcher = null; // Ensure the watcher can't be stopped multiple times
      }
    },
    hydrateContentWithGFM() {
      if (!this.isChunk && this.$refs.content) {
        this.$nextTick(this.renderGFM(this.$refs.content));
      }
      this.detectScrollableCodeBlocks();
    },
    logEvent(e) {
      this.$emit('track-feedback', {
        ...e,
        message: this.message
      });
    },
    manageMessageUpdate() {
      this.setChunks();
      if (!this.isChunk) {
        this.stopWatchingMessage();
      }
    },
    onInsertCodeSnippet(e) {
      this.$emit('insert-code-snippet', e);
    },
    onCopyCodeSnippet(e) {
      this.$emit('copy-code-snippet', e);
    },
    onGetContextItemContent(contextItem) {
      this.$emit('get-context-item-content', {
        messageId: this.message.id,
        contextItem
      });
    },
    detectScrollableCodeBlocks() {
      const codeBlocks = document.querySelectorAll('.duo-chat-message pre');
      codeBlocks.forEach(e => {
        if (e.scrollHeight > e.offsetHeight) {
          e.classList.add(DUO_CODE_SCRIM_BOTTOM_CLASS);
          e.addEventListener('scroll', throttle(() => this.toggleScrolling(e), 200));
        }
      });
    },
    toggleScrolling(e) {
      if (e.scrollHeight - e.scrollTop <= e.offsetHeight + DUO_CODE_SCRIM_OFFSET) {
        e.classList.remove(DUO_CODE_SCRIM_BOTTOM_CLASS);
      } else {
        e.classList.add(DUO_CODE_SCRIM_BOTTOM_CLASS);
      }
      if (e.scrollTop > DUO_CODE_SCRIM_OFFSET) {
        e.classList.add(DUO_CODE_SCRIM_TOP_CLASS);
      } else {
        e.classList.remove(DUO_CODE_SCRIM_TOP_CLASS);
      }
    },
    async copyMessage() {
      const hasPermissions = await checkClipboardPermissions();
      if (!this.copied) {
        if (hasPermissions) {
          await navigator.clipboard.writeText(this.message.content);
        } else {
          this.$emit('copy-message', {
            detail: {
              message: this.message.content
            }
          });
        }
        this.copied = true;
      }
    },
    onOpenFilePath(e) {
      this.$emit('open-file-path', e);
    }
  }
};

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"gl-flex gl-flex-row gl-items-end duo-chat-message-container",on:{"mouseenter":function($event){_vm.hovered = true;},"mouseleave":function($event){_vm.hovered = false;
    _vm.copied = false;}}},[(_vm.isAssistantMessage || _vm.isUserMessage)?[_c('div',{staticClass:"duo-chat-message gl-leading-20 gl-break-anywhere md gl-w-full",class:{
        'gl-ml-auto gl-py-3 gl-px-4 gl-bg-feedback-info gl-text-feedback-info': _vm.isUserMessage,
        'gl-text-default': _vm.isAssistantMessage && !_vm.error,
        'duo-chat-message-with-error gl-bg-feedback-danger gl-p-3': _vm.error,
      },on:{"insert-code-snippet":_vm.onInsertCodeSnippet,"copy-code-snippet":_vm.onCopyCodeSnippet}},[(_vm.showA11yFromText)?_c('div',{staticClass:"gl-sr-only"},[_vm._v("\n        "+_vm._s(_vm.isUserMessage ? _vm.$options.i18n.FROM_ME : _vm.$options.i18n.FROM_DUO)+"\n      ")]):_vm._e(),_vm._v(" "),_c('div',{ref:"content-wrapper",class:{ 'has-error': _vm.error }},[(_vm.displaySelectedContextItems && _vm.isAssistantMessage)?_c('duo-chat-context-item-selections',{attrs:{"selections":_vm.selectedContextItems,"title":_vm.selectedContextItemsTitle,"default-collapsed":_vm.selectedContextItemsDefaultCollapsed,"variant":"assistant"},on:{"get-content":_vm.onGetContextItemContent}}):_vm._e(),_vm._v(" "),(_vm.error)?_c('div',{staticClass:"gl-flex"},[(_vm.error)?_c('gl-icon',{staticClass:"error-icon gl-mr-3 gl-mt-1 gl-shrink-0 gl-text-danger",attrs:{"aria-label":_vm.$options.i18n.MESSAGE_ERROR,"name":"error","size":16,"data-testid":"error"}}):_vm._e(),_vm._v(" "),_c('div',{directives:[{name:"safe-html",rawName:"v-safe-html:[$options.safeHtmlConfigExtension]",value:(_vm.renderedError),expression:"renderedError",arg:_vm.$options.safeHtmlConfigExtension}],ref:"error-message"})],1):_c('div',[_c('div',{directives:[{name:"safe-html",rawName:"v-safe-html:[$options.safeHtmlConfigExtension]",value:(_vm.messageContent),expression:"messageContent",arg:_vm.$options.safeHtmlConfigExtension}],ref:"content"}),_vm._v(" "),(_vm.isAssistantMessage)?[(_vm.sources)?_c('documentation-sources',{attrs:{"sources":_vm.sources}}):_vm._e(),_vm._v(" "),_c('div',{staticClass:"duo-chat-message-feedback gl-mt-4 gl-flex gl-items-end"},[(_vm.isChunkAndNotCancelled)?_c('gl-animated-loader-icon',{attrs:{"is-on":true}}):_vm._e(),_vm._v(" "),(_vm.shouldShowFeedbackLink)?_c('message-feedback',{attrs:{"has-feedback":_vm.hasFeedback},on:{"feedback":_vm.logEvent}}):_vm._e()],1)]:_vm._e()],2),_vm._v(" "),(_vm.displaySelectedContextItems && _vm.isUserMessage)?_c('duo-chat-context-item-selections',{attrs:{"selections":_vm.selectedContextItems,"title":_vm.selectedContextItemsTitle,"default-collapsed":_vm.selectedContextItemsDefaultCollapsed,"variant":"user"},on:{"get-content":_vm.onGetContextItemContent}}):_vm._e()],1)]),_vm._v(" "),_c('transition',{attrs:{"name":"duo-chat-message-actions"}},[(_vm.shouldShowCopyAction)?_c('div',{staticClass:"gl-bg-subtle duo-chat-message-actions gl-rounded-tr-lg gl-rounded-br-lg"},[_c('gl-button',{directives:[{name:"gl-tooltip",rawName:"v-gl-tooltip.hover",modifiers:{"hover":true}}],class:{ '!gl-text-success': _vm.copied },attrs:{"title":_vm.copied ? _vm.$options.i18n.CHAT_MESSAGE_COPIED : _vm.$options.i18n.CHAT_MESSAGE_COPY,"icon":_vm.copied ? 'check-circle-filled' : 'copy-to-clipboard',"category":"tertiary"},on:{"click":_vm.copyMessage}})],1):_vm._e()])]:_c('message-map',{attrs:{"message":_vm.message,"with-feedback":_vm.withFeedback,"data-testid":"workflow-message"},on:{"open-file-path":_vm.onOpenFilePath,"feedback":_vm.logEvent,"insert-code-snippet":_vm.onInsertCodeSnippet,"copy-code-snippet":_vm.onCopyCodeSnippet}})],2)};
var __vue_staticRenderFns__ = [];

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = __vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

export default __vue_component__;
export { i18n };
